import { BaseComponent } from 'components/BaseComponent';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { HBox, VBox } from '../../../components/Box';
import { Check, Times } from '../../../components/Icon';
import { Modal, ModalControls } from '../../../components/Modal';
import { FormField, FormFields } from '../../../components/FormFields';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Buttons';
import { deleteCompanyProperty, setCompanyProperty } from '../actions';
import _ from 'lodash';
import { isNumeric } from '../../../utils/string';
import { selectCompanyPropertyNames } from '../selectors';
import { Dropdown } from '../../../components/Dropdown';
import { COMPANY_PROPERTY_NAMES } from '../constants';
import { Checkbox } from '../../../components/Checkbox';
import {Permission} from "../../../components/Permission";
import {
  COMPANY_EDIT_CUSTOMERNUMBER,
  COMPANY_EDIT_IBAN,
  COMPANY_EDIT_BTW_NUMBER,
  COMPANY_EDIT_COMPANY_TYPE,
  COMPANY_EDIT_COMPANY_FROM_EU,
  COMPANY_EDIT_VINDAT_PAYMENTCONDITION,
  EDIT_COMPANY_STATUS,
  COMPANY_PARENT_EXACT
} from "../../../components/Permission/constants";

const STATE = {
  edit: null,
  value: '',
  add: false,
  deletePropertyConfirm: null,
  addPropertyName: null,
  addPropertyValue: '',
};

const Container = styled.div`
  .company-property-row {
    display: flex;
    align-items: center;
    width: 500px;
    height: 57px;

    > div {
      width: 200px;
      flex: 2;

      &:last-child {
        max-width: 100px;
        text-align: right;
        flex: 1;
      }
    }
  }
  @media(max-width: 768px){
    .company-property-row {
      width: 100%;
      > div {
        width: 140px;

        &:last-child {
          width: 40px;
        }
      }
    }
  }
`;

const parsePropertyValue = function(value) {
  //todo: move parsing to API
  //basic parsing ... (should probably be done at api level?)

  if (value === 'false') value = false;
  if (value === 'true') value = true;
  if (isNumeric(value)) value = parseFloat(value);
  return value;
};

const Property = styled.div`
  > .box {
    align-items: center;
    border-bottom: 1px solid #ddd;
  }
  .name {
    margin-right: 12px;
    width: 200px;
  }
`;

/* eslint-disable react/prefer-stateless-function */
class CompanyPropertiesEditorComp extends BaseComponent {
  constructor(props) {
    super(props, { ...STATE });
  }

  componentDidMount() {
    this.props.fetch();
  }

  editProperty(property) {
    this.setState({ edit: property.id, value: property.value ?? '' });
  }

  reset() {
    this.setState({ ...STATE });
  }

  get company() {
    return this.props.company;
  }

  save() {
    let value = parsePropertyValue(this.state.value);

    this.props.save(this.company.properties.find(p => p.id === this.state.edit).name.name, value, this.reset.bind(this));
  }

  add() {
    this.props.save(this.state.addPropertyName, this.state.addPropertyValue, this.reset.bind(this));
  }

  render() {
    const company = this.props.company;
    if (!company) return null;
    return (
      <Container>
        {this.state.add ? this.renderAdd() : null}
        {this.renderDeleteProperty()}
        <h3>{this.lbl('company.details.properties')}:</h3>
        <div>
          <Button small onClick={this.setStateFn({ add: true })}>
            +
          </Button>
        </div>
        <VBox>{company.properties.map(p => this.renderProperty(p, company.id))}</VBox>
      </Container>
    );
  }

  renderAdd() {
    const options = COMPANY_PROPERTY_NAMES.map(name => ({
      label: this.lbl(`company.property.${name.name}`),
      value: name.name,
    }));

    const selected_prop = COMPANY_PROPERTY_NAMES.find(o => o.name == this.state.addPropertyName);
    const selected_option = options.find(o => o.value == this.state.addPropertyName);

    if (!options || !options.length) return null;

    return (
      <Modal onClose={this.reset.bind(this)} title={this.lbl('company.property.add')}>
        <Dropdown options={options} onChange={o => this.setState({ addPropertyName: o.value })} value={selected_option} />
        {selected_prop ? (
          selected_prop.type == 'boolean' ? (
            <Checkbox
              onChange={this.setStateFn({ addPropertyValue: !this.state.addPropertyValue })}
              checked={this.state.addPropertyValue}
            />
          ) : (
            <FormField>
              <Input name="addPropertyValue" value={this.state.addPropertyValue} onChange={this.onInputChange.bind(this)} />
            </FormField>
          )
        ) : null}
        <ModalControls>
          <Button primary disabled={!selected_prop} onClick={this.add.bind(this)}>
            {this.lbl('save')}
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }

  renderProperty(property, companyId) {
    const value = property.value;
    console.log(property);

    if (this.state.edit === property.id) return this.renderEditProperty(property);
    const propertyComponent = <Property key={`${property.id}`} onClick={() => this.editProperty(property)}>
      <HBox className="company-property-row">
        <div className="name">{property.name.name}</div>
        <div className="value">{value === true ? <Check /> : value === false ? <Times /> : value}</div>
        <div className="name">
          <Button small onClick={this.setStateFn({ deletePropertyConfirm: { propertyId: property.id, companyId } })}>
            <Times />
          </Button>
        </div>
      </HBox>
    </Property>;

    const proxyComponent =  <Property key={`${property.id}`}>
      <HBox className="company-property-row">
        <div className="name">{property.name.name}</div>
        <div className="value">{value === true ? <Check /> : value === false ? <Times /> : value}</div>
        <div className="name"></div>
      </HBox>
    </Property>;

    switch(property?.name?.name){
      case 'exact_sync_to_parent':
        return (
          <Permission permission={COMPANY_PARENT_EXACT} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'company_type':
        return (
          <Permission permission={COMPANY_EDIT_COMPANY_TYPE} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'status':
        return (
          <Permission permission={EDIT_COMPANY_STATUS} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'company_from_eu':
        return (
          <Permission permission={COMPANY_EDIT_COMPANY_FROM_EU} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'vindat_paymentcondition':
        return (
          <Permission permission={COMPANY_EDIT_VINDAT_PAYMENTCONDITION} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'customernumber':
        return (
          <Permission permission={COMPANY_EDIT_CUSTOMERNUMBER} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'BTW_number':
        return (
          <Permission permission={COMPANY_EDIT_BTW_NUMBER} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      case 'IBAN':
        return (
          <Permission permission={COMPANY_EDIT_IBAN} proxy={proxyComponent}>
            {propertyComponent}
          </Permission>
        );
      default: return propertyComponent;
    }
  }

  renderEditProperty(property) {
    return (
      <Modal key={property.id} onClose={this.reset.bind(this)} title={this.lbl('company.property.edit')}>
        <FormFields>
          <h1>{property.name.name}</h1>
          <FormField>
            <Input value={this.state.value} name="value" onChange={this.onInputChange.bind(this)} />
          </FormField>
        </FormFields>
        <ModalControls>
          <Button primary onClick={this.save.bind(this)}>
            Save
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }

  renderDeleteProperty() {
    if (!this.state.deletePropertyConfirm) return null;
    return (
      <Modal onClose={this.reset.bind(this)} title={this.lbl('company.modal.delete.confirm.title')}>
        <ModalControls>
          <Button
            isPrimary
            onClick={() => {
              this.props.deleteCompanyProperty(
                this.state.deletePropertyConfirm.propertyId,
                this.state.deletePropertyConfirm.companyId,
                this.reset.bind(this),
              );
            }}
          >
            {this.lbl('confirm')}
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }
}

CompanyPropertiesEditorComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    companyPropertyNames: selectCompanyPropertyNames(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    fetch() {},
    save(property_name, value, cb) {
      dispatch(setCompanyProperty(ownProps.company.id, property_name, value, cb));
    },
    deleteCompanyProperty(propertyId, companyId, callback) {
      dispatch(deleteCompanyProperty(propertyId, companyId, callback));
    },
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export const CompanyPropertiesEditor = compose(
  withConnect,
  withRouter,
)(CompanyPropertiesEditorComp);
